import { LoadingStateModel } from '@/models/loadingState';
import CaseService from '@/services/caseService';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useStudyStore } from './study';
import { PumpPipelineFilter, type CaseModel } from '@/models/case';
import { TableColumnType, type TableColumnModel } from '@/components/common/table/interfaces/TableColumnModel';
import { useFormStore } from './forms';
import { createRequestToken } from './requestToken';
import { pageChangeFrom } from './listControls';
import type { PaginatorData } from '@/components/common/interfaces/PaginatorData';
import { FormModel } from '@/models';
import { LocalStorageUtils } from '@/utils/localStorageUtils';


export const usePipelineStore = defineStore('pipeline', () => {

  const tableColumns = ref<TableColumnModel[]>([
    {
      id: 1,
      header: 'Case Label',
      key: 'label',
      type: TableColumnType.TEXT_WITH_ICON,
      /*
       * No `icon`. `CaseLabelCell.vue` draws this column's mark itself, from whether the case is
       * finished — which is a fact about the row and not about the column, so a column-level icon
       * could only ever say one of the two things. It also takes one declaration of the SP folder
       * blue out of the app: the value is hard-coded here, in `stores/case.ts` and in
       * `models/archive.ts`, and this was the only one of the three the pipeline read.
       */
      sortable: false,
      frozen: true
    }
  ]);

  const formStore = useFormStore();
  const studyStore = useStudyStore();
  const caseList = ref<CaseModel[]>([]);
  const totalCasesCount = ref<number>(0);
  const currentPage = ref(1);
  /**
   * How many rows the grid last measured room for.
   *
   * A seed rather than a setting. The grid measures the space it has and reports what fits through
   * `setPageCapacity`, and the answer is kept across reloads — so this literal is only ever what
   * the very first page load on a machine goes out with, before anything has been measured. Ten,
   * because a page that turns out to be too long costs a scrollbar and one that turns out to be too
   * short costs nothing at all.
   */
  const measuredPageSize = ref(LocalStorageUtils.getPipelinePageSize() ?? 10);
  /**
   * The page size the reader asked for instead, or `undefined` to let the measurement stand.
   *
   * Kept apart from the measurement rather than overwriting it, because the two answer different
   * questions — what this window fits, and what this clinician wants — and only the second should
   * follow them from a laptop to a monitor.
   */
  const pageSizeOverride = ref(LocalStorageUtils.getPipelinePageSizeOverride());
  /**
   * How many cases a page asks for: the reader's size where they picked one, the measured size
   * where they did not.
   *
   * Written rather than computed from the two above, because the paginator can change it too — see
   * `onPageChange` — and a computed page size would have nowhere to put that.
   */
  const pageLimit = ref(pageSizeOverride.value ?? measuredPageSize.value);
  const searchText = ref('');

  const isPumpStudy = computed(() => studyStore.selectedStudy?.flags?.customDataDictionary === 'PUMP');
  const pumpFilter = ref<PumpPipelineFilter | undefined>(PumpPipelineFilter.ACTIVE);

  const buildTableFormColumns = async () => {
    if (formStore.formList.length == 0) {
      await formStore.getStudyForms();
    }

    if (tableColumns.value.length === 1) {
      formStore.formList.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      formStore.formList.forEach((form, index) => {
        tableColumns.value.push({
          id: index + 2,
          header: form.title,
          key: form.identifier,
          type: TableColumnType.PIPELINE,
          sortable: false,
          identifier: form.identifier
        });
      });
    }
  };

  const casesLoader = ref(new LoadingStateModel({isLoading: false}));
  const pipelineRequest = createRequestToken();

  const getPipelineCases = async () => {
    const token = pipelineRequest.claim();
    casesLoader.value.startLoading();
    await buildTableFormColumns();
    try {
      let filter = pumpFilter.value;
      if (!isPumpStudy.value) {
        filter = undefined;
      }
      const caseResponse =
        await CaseService.getCases(
          studyStore.selectedStudyId!,
          currentPage.value,
          pageLimit.value,
          searchText.value,
          'pipeline',
          'createdAt',
          undefined,
          filter
        );
      if (!pipelineRequest.holds(token)) return;
      caseList.value = caseResponse.cases;
      totalCasesCount.value = caseResponse.casesCount;
    } catch (error) {
      if (!pipelineRequest.holds(token)) return;
      casesLoader.value.setError(error);
    } finally {
      if (pipelineRequest.holds(token)) {
        casesLoader.value.stopLoadingAndFinish();
        await mapFormEnforcements(formStore.formList);
      }
    }
  };

  /**
   * Reloads a single case in place. Used when an entry is saved without the pipeline unmounting (the entry dialog),
   * where a full `getPipelineCases()` would replace every row object and so drop the user's selection and scroll
   * position.
   *
   * Goes through the paginated cases endpoint in `pipeline` mode rather than `getCaseEntries`, because only that mode
   * computes the per-entry `completion` the pipeline cells render their progress bars from.
   */
  const refreshCase = async (caseIdentifier: string) => {
    const index = caseList.value.findIndex(caseItem => caseItem.identifier === caseIdentifier);
    if (index === -1) {
      // The case isn't on the current page (or the list was never loaded) — nothing to patch.
      return;
    }

    try {
      // Mirror the filter and sort the visible list was built with, so the refreshed row stays consistent with it.
      const filter = isPumpStudy.value ? pumpFilter.value : undefined;
      // A single row is all we need: case labels are unique within a study, so the label narrows the search to this
      // case and keeps the (fairly expensive) pipeline-mode entry and completion work down to one case's worth.
      const caseResponse = await CaseService.getCases(
        studyStore.selectedStudyId!,
        1,
        1,
        caseList.value[index].label,
        'pipeline',
        'createdAt',
        undefined,
        filter
      );

      // The endpoint matches labels as a substring, so confirm the row we got back really is ours before patching.
      const refreshedCase = caseResponse.cases.find(caseItem => caseItem.identifier === caseIdentifier);
      if (!refreshedCase) {
        // The case no longer matches the active filter, or another label contains this one and sorted ahead of it.
        // Leaving the row as-is beats patching it with a different case's data.
        return;
      }

      // Patch the existing row object rather than replacing it. The pipeline table has no `dataKey`, so PrimeVue
      // matches selected rows by deep equality — swapping in a new object with updated entries would no longer match
      // the stale object held in the selection and the row would silently lose its checkbox.
      Object.assign(caseList.value[index], refreshedCase);
      await mapFormEnforcements(formStore.formList);
    } catch (error) {
      casesLoader.value.setError(error);
    }
  };

  /**
   * Put a filter in place without reading. `DataSection` seeds a `?caseFilter=` URL's filter through
   * this before the grid mounts, so the grid's own first read already carries it.
   */
  const seedSearch = (searchQuery: string) => {
    currentPage.value = 1;
    searchText.value = searchQuery;
  };

  const handleCaseSearch = (searchQuery: string) => {
    seedSearch(searchQuery);
    getPipelineCases();
  };

  /**
   * An order-locked form with nothing in it locks every form after it, for that case.
   *
   * Both lookups below are optional, and both used to throw. `caseItem.entries` is keyed only by
   * the forms a case actually has entries on — `CaseModel.mapFromBackend` builds it from the
   * entries themselves — so the empty order-locked form this looks for is exactly the key that
   * is missing, and reading `.length` off it threw on the first case that had one. The throw left
   * the section's error toast up and the cascade unapplied, which is why a form waiting its turn
   * drew as available.
   */
  const mapFormEnforcements = async (forms: FormModel[]) => {
    for (const caseItem of caseList.value) {
      for (let i = 0; i < forms.length; i++) {
        const form = forms[i];
        if (caseItem.enforcements?.[form.identifier]?.enforceOrder) {
          if (!caseItem.entries?.[form.identifier]?.length) {
            forms.forEach((formItem, index) => {
              const enforcements = caseItem.enforcements?.[formItem.identifier];
              if (index > i && formItem.identifier !== form.identifier && enforcements) {
                enforcements.enforceOrder = true;
              }
            });
          }
          break;
        }
      }
    }
  };

  /**
   * Move to a page size and read the page it puts the reader on.
   *
   * The page is moved rather than reset, so neither a resize nor a change of size throws the reader
   * back to the top of a list they had paged into: the first record on screen stays on screen, in
   * whichever page now holds it.
   */
  const applyPageSize = (size: number) => {
    if (size === pageLimit.value) {
      return;
    }
    const firstRecord = (currentPage.value - 1) * pageLimit.value;
    pageLimit.value = size;
    currentPage.value = Math.floor(firstRecord / size) + 1;
    getPipelineCases();
  };

  /**
   * Take the page size the grid has room for, and read that page unless the reader has asked for
   * another.
   *
   * The table measures its own box — see `autoRows` in `Table.vue` — because only it knows how much
   * of the window is left after the section's header, the column headings and the paginator. The
   * decision about what to *do* with the number is here, because it is a request, and the grid
   * makes none.
   *
   * The measurement is recorded either way, choice or no choice. It is what clearing the choice
   * goes back to. Remembering it is what makes that instant, rather than a second round of measure
   * and then read.
   */
  const setPageCapacity = (capacity: number) => {
    measuredPageSize.value = capacity;
    LocalStorageUtils.setPipelinePageSize(capacity);
    if (pageSizeOverride.value !== undefined) {
      return;
    }
    applyPageSize(capacity);
  };

  /**
   * Take the page size the reader asked for, or `undefined` to hand the decision back to the grid.
   *
   * Why a reader would want one is on `PAGE_SIZE_CHOICES`. What matters here is that the choice
   * outranks every later measurement until they take it back.
   */
  const setPageSizeOverride = (rows?: number) => {
    pageSizeOverride.value = rows;
    LocalStorageUtils.setPipelinePageSizeOverride(rows);
    applyPageSize(rows ?? measuredPageSize.value);
  };

  const onPageChange = (pageData: PaginatorData) => {
    const change = pageChangeFrom(pageData, currentPage.value, pageLimit.value);
    if (!change) {
      return;
    }
    currentPage.value = change.page;
    pageLimit.value = change.limit;
    getPipelineCases();
  };

  const resetStore = () => {
    tableColumns.value.splice(1, tableColumns.value.length - 1);
    currentPage.value = 1;
  };

  return {
    getPipelineCases,
    refreshCase,
    onPageChange,
    setPageCapacity,
    setPageSizeOverride,
    handleCaseSearch,
    seedSearch,
    resetStore,
    searchText,
    tableColumns,
    casesLoader,
    caseList,
    totalCasesCount,
    currentPage,
    pageLimit,
    pageSizeOverride,
    pumpFilter,
    isPumpStudy
  };
});
